/**
 * iter94 — Multi-country partner tax fields.
 *
 * Renders Tax Country + Contractor Type pickers, then conditionally
 * renders the country-specific field set:
 *   - US     → Legal Business Name + Tax ID (EIN/SSN, full)
 *   - CA     → SIN (full → only last 3 stored) OR BN + Corporation name
 *   - OTHER  → Foreign Tax ID + Business Registration
 *
 * Used by:
 *   - BecomeAPartner.jsx (signup — controlled inputs)
 *   - PartnerDashboard.jsx (self-edit — Tax Details card)
 *   - AdminAccountDetail.jsx (admin edit modal)
 *
 * For admin/self-edit views, the parent passes `mode="edit"` so the
 * already-stored masked values render as helper text under each input.
 */
import React from 'react';

export const TAX_COUNTRY_OPTIONS = [
  { value: 'US',    label: 'United States' },
  { value: 'CA',    label: 'Canada' },
  { value: 'OTHER', label: 'Other / International' },
];

export const CONTRACTOR_TYPE_OPTIONS = [
  { value: 'individual',  label: 'Individual / Sole Proprietor' },
  { value: 'business',    label: 'Business / LLC' },
  { value: 'corporation', label: 'Corporation' },
];

const fieldLabel = (txt) => (
  <div className="uppercase tracking-[0.16em] text-[10px] text-[hsl(var(--primary)/0.6)] font-medium mb-1">{txt}</div>
);

export function TaxFieldsForm({ value, onChange, masked = null, mode = 'edit' }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const country = value.tax_country || 'US';
  const ctype   = value.tax_contractor_type || '';

  return (
    <div className="space-y-5" data-testid="tax-fields-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          {fieldLabel('Tax Country')}
          <select
            value={country}
            onChange={(e) => set('tax_country', e.target.value)}
            className="w-full bg-[hsl(var(--background))] border border-[hsl(var(--primary)/0.2)] focus:border-[hsl(var(--accent))] focus:outline-none rounded-md px-3 py-2.5 text-sm"
            data-testid="tax-country-select"
          >
            {TAX_COUNTRY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>

        <label className="block">
          {fieldLabel('Contractor Type')}
          <select
            value={ctype}
            onChange={(e) => set('tax_contractor_type', e.target.value || null)}
            className="w-full bg-[hsl(var(--background))] border border-[hsl(var(--primary)/0.2)] focus:border-[hsl(var(--accent))] focus:outline-none rounded-md px-3 py-2.5 text-sm"
            data-testid="tax-contractor-type-select"
          >
            <option value="">Select…</option>
            {CONTRACTOR_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
      </div>

      {/* USA fields */}
      {country === 'US' && (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="tax-fields-us">
          <label className="block">
            {fieldLabel('Legal Business Name')}
            <input
              type="text"
              value={value.legal_business_name || ''}
              onChange={(e) => set('legal_business_name', e.target.value)}
              placeholder="Anderson Memorials LLC"
              className="w-full bg-[hsl(var(--background))] border border-[hsl(var(--primary)/0.2)] focus:border-[hsl(var(--accent))] focus:outline-none rounded-md px-3 py-2.5 text-sm"
              data-testid="tax-legal-business-name"
            />
          </label>
          <label className="block">
            {fieldLabel('Tax ID (EIN or SSN)')}
            <input
              type="text"
              value={value.tax_id || ''}
              onChange={(e) => set('tax_id', e.target.value)}
              placeholder={masked?.tax_id_masked || '12-3456789'}
              autoComplete="off"
              className="w-full bg-[hsl(var(--background))] border border-[hsl(var(--primary)/0.2)] focus:border-[hsl(var(--accent))] focus:outline-none rounded-md px-3 py-2.5 text-sm font-mono"
              data-testid="tax-id-input"
            />
            {masked?.tax_id_masked && mode === 'edit' && (
              <p className="text-[11px] text-[hsl(var(--primary)/0.65)] mt-1.5">
                Current on file: <span className="font-mono">{masked.tax_id_masked}</span>. Leave blank to keep unchanged.
              </p>
            )}
          </label>
        </div>
      )}

      {/* Canada fields */}
      {country === 'CA' && (
        <div className="space-y-4" data-testid="tax-fields-ca">
          {ctype !== 'corporation' && (
            <label className="block">
              {fieldLabel('SIN — Social Insurance Number')}
              <input
                type="text"
                value={value.tax_sin || ''}
                onChange={(e) => set('tax_sin', e.target.value)}
                placeholder={masked?.tax_sin_masked || '123-456-789'}
                autoComplete="off"
                className="w-full bg-[hsl(var(--background))] border border-[hsl(var(--primary)/0.2)] focus:border-[hsl(var(--accent))] focus:outline-none rounded-md px-3 py-2.5 text-sm font-mono"
                data-testid="tax-sin-input"
              />
              <p className="text-[11px] text-[hsl(var(--primary)/0.7)] mt-1.5 leading-relaxed">
                For privacy, <strong>only the last 3 digits</strong> are stored — the full number is discarded after submission.
                {masked?.tax_sin_masked && mode === 'edit' && (
                  <> Current on file: <span className="font-mono">{masked.tax_sin_masked}</span>.</>
                )}
              </p>
            </label>
          )}
          {(ctype === 'corporation' || ctype === 'business') && (
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block">
                {fieldLabel('Business Number (BN)')}
                <input
                  type="text"
                  value={value.tax_business_number || ''}
                  onChange={(e) => set('tax_business_number', e.target.value)}
                  placeholder="123456789RT0001"
                  autoComplete="off"
                  className="w-full bg-[hsl(var(--background))] border border-[hsl(var(--primary)/0.2)] focus:border-[hsl(var(--accent))] focus:outline-none rounded-md px-3 py-2.5 text-sm font-mono"
                  data-testid="tax-bn-input"
                />
                <p className="text-[11px] text-[hsl(var(--primary)/0.6)] mt-1.5">
                  9 digits (123456789) or full form (123456789RT0001).
                </p>
              </label>
              <label className="block">
                {fieldLabel('Corporation Name')}
                <input
                  type="text"
                  value={value.tax_corporation_name || ''}
                  onChange={(e) => set('tax_corporation_name', e.target.value)}
                  placeholder="Maple Memorials Ltd."
                  className="w-full bg-[hsl(var(--background))] border border-[hsl(var(--primary)/0.2)] focus:border-[hsl(var(--accent))] focus:outline-none rounded-md px-3 py-2.5 text-sm"
                  data-testid="tax-corp-name-input"
                />
              </label>
            </div>
          )}
        </div>
      )}

      {/* Other / International fields */}
      {country === 'OTHER' && (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="tax-fields-other">
          <label className="block">
            {fieldLabel('Foreign Tax ID')}
            <input
              type="text"
              value={value.tax_foreign_id || ''}
              onChange={(e) => set('tax_foreign_id', e.target.value)}
              placeholder={masked?.tax_foreign_id_masked || 'Tax/VAT number'}
              autoComplete="off"
              className="w-full bg-[hsl(var(--background))] border border-[hsl(var(--primary)/0.2)] focus:border-[hsl(var(--accent))] focus:outline-none rounded-md px-3 py-2.5 text-sm font-mono"
              data-testid="tax-foreign-id-input"
            />
            {masked?.tax_foreign_id_masked && mode === 'edit' && (
              <p className="text-[11px] text-[hsl(var(--primary)/0.65)] mt-1.5">
                Current on file: <span className="font-mono">{masked.tax_foreign_id_masked}</span>. Leave blank to keep unchanged.
              </p>
            )}
          </label>
          <label className="block">
            {fieldLabel('Business Registration')}
            <input
              type="text"
              value={value.tax_business_registration || ''}
              onChange={(e) => set('tax_business_registration', e.target.value)}
              placeholder="Companies House / registry number"
              className="w-full bg-[hsl(var(--background))] border border-[hsl(var(--primary)/0.2)] focus:border-[hsl(var(--accent))] focus:outline-none rounded-md px-3 py-2.5 text-sm"
              data-testid="tax-business-registration-input"
            />
          </label>
        </div>
      )}

      <p className="text-[11px] text-[hsl(var(--primary)/0.6)] leading-relaxed pt-1">
        Tax information is used solely for required regulatory filings (US 1099, Canadian T4A, equivalent). Sensitive IDs are encrypted at rest and never shown again in full after save.
      </p>
    </div>
  );
}

/**
 * MaskedTaxSummary — read-only display block for admin/dashboard
 * panels. Renders the masked last-3/4 values and presence flags.
 */
export function MaskedTaxSummary({ masked }) {
  if (!masked) return null;
  return (
    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm" data-testid="masked-tax-summary">
      <Row label="Tax Country"     value={masked.tax_country_label || masked.tax_country || '—'} />
      <Row label="Contractor Type" value={masked.tax_contractor_type_label || '—'} />
      {masked.tax_country === 'US' && (
        <>
          <Row label="Legal Business Name" value={masked.legal_business_name || '—'} />
          <Row label="Tax ID" value={masked.tax_id_masked || (masked.has_tax_id ? '••••' : '—')} mono />
        </>
      )}
      {masked.tax_country === 'CA' && (
        <>
          <Row label="SIN" value={masked.tax_sin_masked || '—'} mono />
          <Row label="Business Number" value={masked.tax_business_number || '—'} mono />
          <Row label="Corporation Name" value={masked.tax_corporation_name || '—'} />
        </>
      )}
      {masked.tax_country === 'OTHER' && (
        <>
          <Row label="Foreign Tax ID" value={masked.tax_foreign_id_masked || (masked.has_foreign_tax_id ? '••••' : '—')} mono />
          <Row label="Business Registration" value={masked.tax_business_registration || '—'} />
        </>
      )}
      {masked.tax_fields_updated_at && (
        <Row
          label="Last updated"
          value={new Date(masked.tax_fields_updated_at).toLocaleString()}
        />
      )}
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div>
      <div className="uppercase tracking-[0.16em] text-[10px] text-[hsl(var(--primary)/0.55)] font-medium">{label}</div>
      <div className={`text-[hsl(var(--primary))] mt-1 ${mono ? 'font-mono break-all text-xs' : 'text-sm'}`}>{value}</div>
    </div>
  );
}
